import { useState, type FormEvent } from 'react';
import { SaveIcon, TriangleAlertIcon } from 'lucide-react';
import type {
  IncidentErnst,
  IncidentGegevenssoort,
  IncidentGroep,
  IncidentMaatregel,
  IncidentStatus,
  SecurityIncident,
  TriageSecurityIncidentInput,
} from '@kennisbank/shared';
import { ApiError, apiPost } from '../../api/client.js';
import {
  ERNST_LABEL,
  GEGEVENSSOORT_LABEL,
  GEGEVENSSOORT_VOLGORDE,
  GROEP_LABEL,
  GROEP_VOLGORDE,
  STATUS_LABEL,
} from './labels.js';
import { apDeadline, formatMoment } from './termijn.js';

/**
 * Het oordeel van de privacy-contactpersoon. `isDatalek` is een expliciete
 * driewegkeuze: "nog niet beoordeeld" is iets anders dan "beoordeeld, geen
 * datalek" (schema-comment bij `IncidentTriage`), en alleen de eerste optie
 * laat de melding in de wachtrij staan als onbeoordeeld.
 *
 * De correctie op groepen en gegevenssoorten overschrijft de opgave van de
 * melder niet: `melderOpgave` blijft ongewijzigd naast dit oordeel staan.
 */

/**
 * Een maatregel telt pas als hij compleet is: omschrijving, eigenaar én
 * einddatum. Half ingevuld opslaan zou een maatregel opleveren waar niemand
 * op aan te spreken is, en dat is precies wat de evaluatie moet voorkomen.
 */
type MaatregelVeld = {
  omschrijving: string;
  wie: string;
  deadline: string;
  afgerondOp: string;
};

function naarMaatregel(m: IncidentMaatregel | undefined): MaatregelVeld {
  return {
    omschrijving: m?.omschrijving ?? '',
    wie: m?.wie ?? '',
    deadline: m?.deadline ?? '',
    afgerondOp: m?.afgerondOp ?? '',
  };
}

function uitMaatregel(v: MaatregelVeld): IncidentMaatregel | undefined {
  const omschrijving = v.omschrijving.trim();
  const wie = v.wie.trim();
  if (!omschrijving || !wie || !v.deadline) return undefined;
  return {
    omschrijving,
    wie,
    deadline: v.deadline,
    ...(v.afgerondOp ? { afgerondOp: v.afgerondOp } : {}),
  };
}

/** Iets ingevuld maar niet alles: dan blokkeren we het opslaan. */
function maatregelHalf(v: MaatregelVeld): boolean {
  const gevuld = [v.omschrijving.trim(), v.wie.trim(), v.deadline].filter(Boolean).length;
  return gevuld > 0 && gevuld < 3;
}

function MaatregelBlok({
  kop,
  uitleg,
  naam,
  waarde,
  zet,
  half,
  veldClass,
}: {
  kop: string;
  uitleg: string;
  naam: string;
  waarde: MaatregelVeld;
  zet: (v: MaatregelVeld) => void;
  half: boolean;
  veldClass: string;
}): JSX.Element {
  return (
    <fieldset className="mt-4 rounded-xl border border-ring2/70 p-3">
      <legend className="px-1 text-[13px] font-semibold text-ink-900">{kop}</legend>
      <p className="text-[12px] text-ink-900/60">{uitleg}</p>
      <label className="mt-2 flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
        Maatregel
        <textarea
          value={waarde.omschrijving}
          onChange={(e) => zet({ ...waarde, omschrijving: e.target.value })}
          rows={2}
          className={veldClass}
        />
      </label>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Wie
          <input
            type="text"
            name={`${naam}-wie`}
            value={waarde.wie}
            onChange={(e) => zet({ ...waarde, wie: e.target.value })}
            placeholder="Naam of rol"
            className={veldClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Deadline
          <input
            type="date"
            name={`${naam}-deadline`}
            value={waarde.deadline}
            onChange={(e) => zet({ ...waarde, deadline: e.target.value })}
            className={veldClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Afgerond op
          <input
            type="date"
            name={`${naam}-afgerond`}
            value={waarde.afgerondOp}
            onChange={(e) => zet({ ...waarde, afgerondOp: e.target.value })}
            className={veldClass}
          />
        </label>
      </div>
      {half && (
        <p className="mt-2 text-[12px] text-error">
          Vul maatregel, wie en deadline samen in. Een maatregel zonder eigenaar of einddatum is een
          voornemen, geen afspraak.
        </p>
      )}
    </fieldset>
  );
}

/** `undefined` (nog niet bepaald) uit een select met drie waarden. */
type Drieweg = '' | 'ja' | 'nee';

function naarDrieweg(v: boolean | undefined): Drieweg {
  if (v === undefined) return '';
  return v ? 'ja' : 'nee';
}

function uitDrieweg(v: Drieweg): boolean | undefined {
  if (v === '') return undefined;
  return v === 'ja';
}

/** ISO met offset -> waarde voor <input type="datetime-local"> in lokale tijd. */
function naarLokaleInvoer(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TriageFormulier({
  incident,
  onSaved,
}: {
  incident: SecurityIncident;
  onSaved: () => void;
}): JSX.Element {
  const t = incident.triage;
  const [isDatalek, setIsDatalek] = useState<'onbeoordeeld' | 'wel' | 'geen'>(
    t?.isDatalek === true ? 'wel' : t?.isDatalek === false ? 'geen' : 'onbeoordeeld',
  );
  const [ernst, setErnst] = useState<IncidentErnst | ''>(t?.ernst ?? '');
  const [groepen, setGroepen] = useState<IncidentGroep[]>(
    t?.groepen ?? incident.melderOpgave.groepen,
  );
  const [gegevenssoorten, setGegevenssoorten] = useState<IncidentGegevenssoort[]>(
    t?.gegevenssoorten ?? incident.melderOpgave.gegevenssoorten,
  );
  const [apMeldingNodig, setApMeldingNodig] = useState<Drieweg>(naarDrieweg(t?.apMeldingNodig));
  const [apMeldingMotivering, setApMeldingMotivering] = useState(t?.apMeldingMotivering ?? '');
  const [apGemeldOp, setApGemeldOp] = useState(naarLokaleInvoer(t?.apGemeldOp));
  const [betrokkenen, setBetrokkenen] = useState<Drieweg>(naarDrieweg(t?.betrokkenenGeinformeerd));
  const [notitie, setNotitie] = useState(t?.behandelaarNotitie ?? '');
  const [titel, setTitel] = useState(t?.titel ?? '');
  const [korteTermijn, setKorteTermijn] = useState<MaatregelVeld>(naarMaatregel(t?.korteTermijn));
  const [langeTermijn, setLangeTermijn] = useState<MaatregelVeld>(naarMaatregel(t?.langeTermijn));
  const [status, setStatus] = useState<IncidentStatus>(incident.status);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const wordtDatalek = isDatalek === 'wel' && t?.isDatalek !== true;
  const deadline = apDeadline(incident.melderOpgave.datumOntdekt);
  // Een keuze over de meldplicht zonder onderbouwing maakt het register
  // waardeloos op het moment dat de AP erom vraagt — juist een "nee" moet
  // uitgelegd zijn, want die zet de escalatie stil.
  const motiveringOntbreekt = apMeldingNodig !== '' && apMeldingMotivering.trim().length === 0;
  const maatregelOnvolledig = maatregelHalf(korteTermijn) || maatregelHalf(langeTermijn);
  const blokkeer = motiveringOntbreekt || maatregelOnvolledig;

  function toggle<T>(lijst: T[], waarde: T, zet: (v: T[]) => void): void {
    zet(lijst.includes(waarde) ? lijst.filter((x) => x !== waarde) : [...lijst, waarde]);
  }

  async function opslaan(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (blokkeer) return;
    setBusy(true);
    setErr(null);
    setOk(false);
    const body: TriageSecurityIncidentInput = {
      isDatalek: isDatalek === 'onbeoordeeld' ? null : isDatalek === 'wel',
      ...(ernst ? { ernst } : {}),
      ...(groepen.length > 0 ? { groepen } : {}),
      ...(gegevenssoorten.length > 0 ? { gegevenssoorten } : {}),
      ...(uitDrieweg(apMeldingNodig) === undefined
        ? {}
        : { apMeldingNodig: uitDrieweg(apMeldingNodig) }),
      ...(apMeldingMotivering.trim() ? { apMeldingMotivering: apMeldingMotivering.trim() } : {}),
      ...(apGemeldOp ? { apGemeldOp: new Date(apGemeldOp).toISOString() } : {}),
      ...(uitDrieweg(betrokkenen) === undefined
        ? {}
        : { betrokkenenGeinformeerd: uitDrieweg(betrokkenen) }),
      ...(notitie.trim() ? { behandelaarNotitie: notitie.trim() } : {}),
      ...(titel.trim() ? { titel: titel.trim() } : {}),
      ...(uitMaatregel(korteTermijn) ? { korteTermijn: uitMaatregel(korteTermijn) } : {}),
      ...(uitMaatregel(langeTermijn) ? { langeTermijn: uitMaatregel(langeTermijn) } : {}),
      status,
    };
    try {
      await apiPost(`/api/v1/beheer/incidents/${incident.id}/triage`, body);
      setOk(true);
      onSaved();
    } catch (e2) {
      setErr(
        e2 instanceof ApiError
          ? `Opslaan mislukt (${e2.status}): ${e2.message}`
          : 'Opslaan mislukt.',
      );
    } finally {
      setBusy(false);
    }
  }

  const veldClass =
    'rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-900/40';

  return (
    <form
      onSubmit={(e) => void opslaan(e)}
      className="rounded-2xl border border-ring2 bg-white p-4 shadow-card"
    >
      <h2 className="text-base font-bold text-ink-900">Triage</h2>
      <p className="mt-1 text-sm text-ink-900/65">
        Je oordeel als privacy-contactpersoon. De wettelijke termijn van 72 uur begint pas zodra je
        de melding kwalificeert als datalek.
      </p>

      <label className="mt-4 flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
        Titel van wat er is gebeurd
        <input
          type="text"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          maxLength={200}
          placeholder="Bijvoorbeeld: verslag per mail naar verkeerde huisartsenpraktijk"
          className={veldClass}
        />
        <span className="text-[12px] font-normal text-ink-900/60">
          Eén regel in je eigen woorden. Dit is wat in de wachtrij en in het register staat; de
          omschrijving van de melder blijft daarnaast ongewijzigd bewaard.
        </span>
      </label>

      <fieldset className="mt-4">
        <legend className="text-[13px] font-semibold text-ink-900">Is dit een datalek?</legend>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-4">
          {(
            [
              ['onbeoordeeld', 'Nog niet beoordeeld'],
              ['wel', 'Ja, dit is een datalek'],
              ['geen', 'Nee, geen datalek'],
            ] as const
          ).map(([waarde, label]) => (
            <label key={waarde} className="flex items-center gap-2 text-sm text-ink-900">
              <input
                type="radio"
                name={`isDatalek-${incident.id}`}
                value={waarde}
                checked={isDatalek === waarde}
                onChange={() => setIsDatalek(waarde)}
                className="h-4 w-4 accent-brand-600"
              />
              {label}
            </label>
          ))}
        </div>
        {wordtDatalek && deadline !== null && (
          <p className="mt-2 inline-flex items-start gap-1.5 rounded-md bg-mosterdgeel-10 px-3 py-1.5 text-[12px] text-ring1-fg">
            <TriangleAlertIcon size={14} className="mt-0.5 shrink-0" />
            Hiermee start de klok: melden aan de Autoriteit Persoonsgegevens vóór{' '}
            {formatMoment(new Date(deadline).toISOString())}.
          </p>
        )}
      </fieldset>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Ernst
          <select
            value={ernst}
            onChange={(e) => setErnst(e.target.value as IncidentErnst | '')}
            className={veldClass}
          >
            <option value="">Nog niet bepaald</option>
            {(Object.keys(ERNST_LABEL) as IncidentErnst[]).map((k) => (
              <option key={k} value={k}>
                {ERNST_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Status van de melding
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as IncidentStatus)}
            className={veldClass}
          >
            {(Object.keys(STATUS_LABEL) as IncidentStatus[]).map((k) => (
              <option key={k} value={k}>
                {STATUS_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="mt-4">
        <legend className="text-[13px] font-semibold text-ink-900">
          Groepen personen (correctie op vraag 6)
        </legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {GROEP_VOLGORDE.map((g) => (
            <label key={g} className="flex items-center gap-2 text-sm text-ink-900">
              <input
                type="checkbox"
                checked={groepen.includes(g)}
                onChange={() => toggle(groepen, g, setGroepen)}
                className="h-4 w-4 accent-brand-600"
              />
              {GROEP_LABEL[g]}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="mt-4">
        <legend className="text-[13px] font-semibold text-ink-900">
          Gegevenssoorten (correctie op vraag 7)
        </legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {GEGEVENSSOORT_VOLGORDE.map((g) => (
            <label key={g} className="flex items-center gap-2 text-sm text-ink-900">
              <input
                type="checkbox"
                checked={gegevenssoorten.includes(g)}
                onChange={() => toggle(gegevenssoorten, g, setGegevenssoorten)}
                className="h-4 w-4 accent-brand-600"
              />
              {GEGEVENSSOORT_LABEL[g]}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Melden aan de Autoriteit Persoonsgegevens
          <select
            value={apMeldingNodig}
            onChange={(e) => setApMeldingNodig(e.target.value as Drieweg)}
            className={veldClass}
          >
            <option value="">Nog niet bepaald</option>
            <option value="ja">Ja, meldplichtig</option>
            <option value="nee">Nee, niet meldplichtig</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
          Datum en tijd van de AP-melding
          <input
            type="datetime-local"
            value={apGemeldOp}
            onChange={(e) => setApGemeldOp(e.target.value)}
            className={veldClass}
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
        Motivering van de meldplicht
        <textarea
          value={apMeldingMotivering}
          onChange={(e) => setApMeldingMotivering(e.target.value)}
          rows={3}
          placeholder="Waarom is deze melding wel of niet meldplichtig? Deze tekst is het antwoord richting de AP en de FG."
          className={veldClass}
        />
        {motiveringOntbreekt && (
          <span className="text-[12px] font-normal text-error">
            Leg vast waarom je wel of niet aan de AP meldt. Ook een gemotiveerde &quot;nee&quot;
            hoort in het register.
          </span>
        )}
      </label>

      <label className="mt-4 flex max-w-sm flex-col gap-1 text-[13px] font-semibold text-ink-900">
        Betrokkenen geïnformeerd
        <select
          value={betrokkenen}
          onChange={(e) => setBetrokkenen(e.target.value as Drieweg)}
          className={veldClass}
        >
          <option value="">Nog niet bepaald</option>
          <option value="ja">Ja</option>
          <option value="nee">Nee</option>
        </select>
      </label>

      <MaatregelBlok
        kop="Korte termijn"
        uitleg="Wat er nu gebeurt om de schade te beperken."
        naam={`kt-${incident.id}`}
        waarde={korteTermijn}
        zet={setKorteTermijn}
        half={maatregelHalf(korteTermijn)}
        veldClass={veldClass}
      />

      <MaatregelBlok
        kop="Lange termijn"
        uitleg="Wat er structureel verandert zodat dit niet opnieuw gebeurt."
        naam={`lt-${incident.id}`}
        waarde={langeTermijn}
        zet={setLangeTermijn}
        half={maatregelHalf(langeTermijn)}
        veldClass={veldClass}
      />

      <label className="mt-4 flex flex-col gap-1 text-[13px] font-semibold text-ink-900">
        Notitie van de behandelaar
        <textarea
          value={notitie}
          onChange={(e) => setNotitie(e.target.value)}
          rows={4}
          placeholder="Wat is er onderzocht, met wie is er contact geweest, welke maatregelen zijn genomen?"
          className={veldClass}
        />
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ring2/70 pt-4">
        <button
          type="submit"
          disabled={busy || blokkeer}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <SaveIcon size={15} /> {busy ? 'Opslaan…' : 'Triage opslaan'}
        </button>
        {ok && !err && <span className="text-[12px] text-success">Opgeslagen.</span>}
        {err && <span className="text-[12px] text-error">{err}</span>}
      </div>
    </form>
  );
}
